import { useState, useEffect, useMemo, useCallback } from 'react';

const DiscountTypeEnum = {
    PERCENTAGE: "percentage",
    FIXED_AMOUNT: "fixed_amount",
    BUY_X_GET_Y: "buy_x_get_y",
    BUNDLE: "bundle",
    TIERED_PRICING: "tiered_pricing", 
};

const AppliesToEnum = {
    ORDER: "order",
    PRODUCT: "product",
    CATEGORY: "category",
    SHIPPING: "shipping",
};

const ApprovalThresholdType = {
    NONE: "none",
    PERCENTAGE_OF_ITEM_VALUE: "percentage_of_item_value",
    FIXED_AMOUNT_OF_DISCOUNT: "fixed_amount_of_discount",
};

export const useDiscountCalculation = (
    cartItems,
    availableDiscountRules,
    selectedCustomer,
    selectedStore,
    couponCodeInputs,      // Can be string (backward compat) or string[]
    originalCartTotal,
    user
) => {
    const [calculatedDiscounts, setCalculatedDiscounts] = useState([]);
    const [totalDiscountAmount, setTotalDiscountAmount] = useState(0);
    const [finalOrderTotal, setFinalOrderTotal] = useState(originalCartTotal);
    const [requiresManagerApproval, setRequiresManagerApproval] = useState(false);
    const [appliedCouponCodeStatus, setAppliedCouponCodeStatus] = useState([]);   // changed to array
    const [discountValidationMessage, setDiscountValidationMessage] = useState([]); // changed to array

    const currentDate = useMemo(() => new Date(), []);

    const calculateDiscounts = useCallback(() => {
        // Backward compatibility: convert string to array if needed
        const couponCodes = Array.isArray(couponCodeInputs) 
            ? couponCodeInputs.filter(Boolean) 
            : [couponCodeInputs].filter(Boolean);

        if (!couponCodes.length || !selectedStore || !cartItems.length) {
            setCalculatedDiscounts([]);
            setTotalDiscountAmount(0);
            setFinalOrderTotal(originalCartTotal);
            setRequiresManagerApproval(false);
            setAppliedCouponCodeStatus([]);
            setDiscountValidationMessage([]);
            return;
        }

        let currentTotalDiscount = 0;
        let overallRequiresApproval = false;
        const appliedRuleIds = new Set();
        const discountsToApplyPayload = [];
        const statusList = [];
        const messageList = [];

        const mutableCartItems = cartItems.map(item => ({
            ...item,
            effectivePrice: item.price,
            itemDiscountAmount: 0,
        }));

        // Process each discount one by one
        couponCodes.forEach((couponCode, index) => {
            // --- PHASE 1: IDENTIFY APPLICABLE RULES for this coupon ---
            const applicableRules = availableDiscountRules.filter(rule => {
                if (rule.requires_coupon_code && rule.coupon_code !== couponCode) return false;
                if (!rule.requires_coupon_code && String(couponCode) !== String(rule.discountrule_id)) return false;

                if (!rule.is_active) return false;
                const startDate = new Date(rule.start_date);
                const endDate = new Date(rule.end_date);
                if (currentDate < startDate || currentDate > endDate) return false;

                const isStoreMatched = rule.is_store_wide || rule.stores.some(s => s.store_id === selectedStore.store_id);
                if (!isStoreMatched) return false;

                const isCustomerMatched = rule.customers.length === 0 || rule.customers.some(c => c.member_id === selectedCustomer?.member_id);
                if (!isCustomerMatched) return false;

                if (rule.minimum_purchase_amount !== null && originalCartTotal < parseFloat(rule.minimum_purchase_amount)) return false;

                const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
                if (rule.minimum_purchase_quantity !== null && totalQuantity < rule.minimum_purchase_quantity) return false;

                return true;
            });

            // Validation UI Status for this discount
            const selectedRule = availableDiscountRules.find(rule =>
                (rule.requires_coupon_code ? rule.coupon_code === couponCode : String(couponCode) === String(rule.discountrule_id))
            );

            if (!selectedRule) {
                statusList[index] = 'invalid';
                messageList[index] = 'Selected discount does not exist.';
            } else if (applicableRules.some(r => r.discountrule_id === selectedRule.discountrule_id)) {
                statusList[index] = 'valid';
                messageList[index] = '';
            } else {
                statusList[index] = 'invalid';
                messageList[index] = 'Unmet conditions for this discount.';
            }

            // --- PHASE 2: APPLY DISCOUNTS ---
            applicableRules.forEach(rule => {
                if (appliedRuleIds.has(rule.discountrule_id)) return;

                let ruleDiscountAmount = 0;
                let originalPriceSegmentForApproval = 0;

                if (rule.discount_type === DiscountTypeEnum.TIERED_PRICING) {
                    mutableCartItems.forEach(item => {
                        let isTargeted = false;
                        if (rule.applies_to === AppliesToEnum.PRODUCT && (item.catentry_id === rule.target_product_id || item.id === rule.target_product_id)) isTargeted = true;
                        else if (rule.applies_to === AppliesToEnum.CATEGORY && item.category_id === rule.target_category_id) isTargeted = true;
                        else if (rule.applies_to === AppliesToEnum.ORDER) isTargeted = true;

                        if (isTargeted && rule.tiers && rule.tiers.length > 0) {
                            const matchedTier = rule.tiers.find(tier => {
                                const min = tier.min_quantity;
                                const max = tier.max_quantity || Infinity;
                                return item.quantity >= min && item.quantity <= max;
                            });

                            if (matchedTier) {
                                const discountPerUnit = parseFloat(matchedTier.tier_value);
                                const totalItemDiscount = discountPerUnit * item.quantity;

                                item.itemDiscountAmount += totalItemDiscount;
                                item.effectivePrice -= discountPerUnit;
                                ruleDiscountAmount += totalItemDiscount;
                                originalPriceSegmentForApproval += (item.price * item.quantity);
                            }
                        }
                    });
                }
                else if (rule.discount_type === DiscountTypeEnum.BUY_X_GET_Y) {
                    const qualifyingItems = mutableCartItems.filter(item =>
                        (rule.applies_to === AppliesToEnum.PRODUCT && (item.catentry_id === rule.target_product_id || item.id === rule.target_product_id)) ||
                        (rule.applies_to === AppliesToEnum.CATEGORY && item.category_id === rule.target_category_id)
                    );
                    if (qualifyingItems.length >= rule.buy_quantity) {
                        const sorted = [...qualifyingItems].sort((a, b) => a.effectivePrice - b.effectivePrice);
                        for (let i = 0; i < rule.get_quantity && i < sorted.length; i++) {
                            const discount = sorted[i].effectivePrice;
                            sorted[i].itemDiscountAmount += discount;
                            sorted[i].effectivePrice -= discount;
                            ruleDiscountAmount += discount;
                            originalPriceSegmentForApproval += sorted[i].price;
                        }
                    }
                }
                else {
                    const isOrderLevel = rule.applies_to === AppliesToEnum.ORDER || rule.applies_to === AppliesToEnum.SHIPPING;
                    if (isOrderLevel) {
                        const subtotal = mutableCartItems.reduce((sum, item) => sum + (item.effectivePrice * item.quantity), 0);
                        ruleDiscountAmount = rule.discount_type === DiscountTypeEnum.PERCENTAGE
                            ? subtotal * (parseFloat(rule.value) / 100)
                            : parseFloat(rule.value);
                        ruleDiscountAmount = Math.min(ruleDiscountAmount, subtotal);
                        originalPriceSegmentForApproval = originalCartTotal;
                    } else {
                        mutableCartItems.forEach(item => {
                            // const isTargeted = (rule.applies_to === AppliesToEnum.PRODUCT && (item.catentry_id === rule.target_product_id || item.id === rule.target_product_id)) ||
                            //     (rule.applies_to === AppliesToEnum.CATEGORY && item.category_id === rule.target_category_id);
                            // if (isTargeted) {
                            //     let discountPerUnit = rule.discount_type === DiscountTypeEnum.PERCENTAGE
                            //         ? item.effectivePrice * (parseFloat(rule.value) / 100)
                            //         : parseFloat(rule.value);
                            //     discountPerUnit = Math.min(discountPerUnit, item.effectivePrice);
                            //     const totalItemDiscount = discountPerUnit * item.quantity;

                            //     item.itemDiscountAmount += totalItemDiscount;
                            //     item.effectivePrice -= discountPerUnit;
                            //     ruleDiscountAmount += totalItemDiscount;
                            //     originalPriceSegmentForApproval += (item.price * item.quantity);
                            // }
                            // Check if the item matches the single target product ID OR exists inside the rule's products array
                            const matchesProductArray = Array.isArray(rule.products) && 
                                rule.products.some(p => p.catentry_id === item.catentry_id || p.catentry_id === item.id);

                            const isTargeted = (rule.applies_to === AppliesToEnum.PRODUCT && 
                                (item.catentry_id === rule.target_product_id || item.id === rule.target_product_id || matchesProductArray)) ||
                                (rule.applies_to === AppliesToEnum.CATEGORY && item.category_id === rule.target_category_id);
                            
                            if (isTargeted) {
                                let discountPerUnit = rule.discount_type === DiscountTypeEnum.PERCENTAGE
                                    ? item.effectivePrice * (parseFloat(rule.value) / 100)
                                    : parseFloat(rule.value);
                                discountPerUnit = Math.min(discountPerUnit, item.effectivePrice);
                                const totalItemDiscount = discountPerUnit * item.quantity;

                                item.itemDiscountAmount += totalItemDiscount;
                                item.effectivePrice -= discountPerUnit;
                                ruleDiscountAmount += totalItemDiscount;
                                originalPriceSegmentForApproval += (item.price * item.quantity);
                            }
                        });
                    }
                }

                if (ruleDiscountAmount > 0) {
                    const requiresApproval = checkApproval(rule, ruleDiscountAmount, originalPriceSegmentForApproval);
                    if (requiresApproval) overallRequiresApproval = true;

                    discountsToApplyPayload.push({
                        discountrule_id: rule.discountrule_id,
                        orders_id: null,
                        customer_id: selectedCustomer?.member_id || null,
                        applied_by_user_id: user?.users_id || null,
                        store_id: selectedStore.store_id,
                        discount_amount_applied: parseFloat(ruleDiscountAmount.toFixed(2)),
                        requires_approval: requiresApproval,
                        approval_status: requiresApproval ? "pending" : "not_required",
                        approved_by_user_id: null,
                        approval_timestamp: null,
                        approval_notes: requiresApproval ? "Discount exceeds approval threshold." : null,
                    });

                    appliedRuleIds.add(rule.discountrule_id);
                    currentTotalDiscount += ruleDiscountAmount;
                }
            });
        });

        setCalculatedDiscounts(discountsToApplyPayload);
        setTotalDiscountAmount(parseFloat(currentTotalDiscount.toFixed(2)));
        setFinalOrderTotal(parseFloat((originalCartTotal - currentTotalDiscount).toFixed(2)));
        setRequiresManagerApproval(overallRequiresApproval);
        setAppliedCouponCodeStatus(statusList);
        setDiscountValidationMessage(messageList);

    }, [cartItems, availableDiscountRules, selectedCustomer, selectedStore, couponCodeInputs, originalCartTotal, currentDate, user]);

    const checkApproval = useCallback((rule, discountAmountApplied, originalPriceSegment) => {
        if (rule.approval_threshold_type === ApprovalThresholdType.NONE) return false;
        if (originalPriceSegment === 0) return false;
        if (rule.approval_threshold_type === ApprovalThresholdType.PERCENTAGE_OF_ITEM_VALUE) {
            return ((discountAmountApplied / originalPriceSegment) * 100) > parseFloat(rule.approval_threshold_value);
        } else if (rule.approval_threshold_type === ApprovalThresholdType.FIXED_AMOUNT_OF_DISCOUNT) {
            return discountAmountApplied > parseFloat(rule.approval_threshold_value);
        }
        return false;
    }, []);

    useEffect(() => {
        calculateDiscounts();
    }, [calculateDiscounts]);

    return {
        calculatedDiscounts,
        totalDiscountAmount,
        finalOrderTotal,
        requiresManagerApproval,
        appliedCouponCodeStatus,      // array now
        discountValidationMessage,    // array now
    };
};